(function () {
  const triggerSelector = '[data-postscript-bis-trigger]';
  const dynamicTriggerSelector = `${triggerSelector}[data-postscript-bis-dynamic="true"]`;
  const activeClass = 'postscript-bis-trigger--active';
  const configElementId = 'PostscriptBackInStockSettings';
  const styleElementId = 'PostscriptBackInStockStyle';
  const hiddenTeaserId = 'postscript-bis-hidden-teaser';
  const productRootSelector = [
    '#ProductGridContainer',
    'advanced-product-recommendations',
    '[data-section-type="product-recommendations"]',
  ].join(', ');

  class PostscriptBackInStock {
    constructor () {
      this.config = this.readConfig();
      this.triggerState = new WeakMap();
      this.observedRoots = new WeakSet();
      this.parsedJson = new Map();
      this.popupIdPromise = null;
      this.popupId = null;
      this.popupResolved = false;
      this.hiddenTeaserPromise = null;
      this.hiddenTeaserKey = null;
      this.refreshQueued = false;
      this.pendingTriggers = new Set();
      this.pendingFullRefresh = false;
      this.popupRefreshChained = false;
      this.listenersActive = false;

      this.handleClick = this.handleClick.bind(this);
      this.handlePress = this.handlePress.bind(this);
      this.warmQueued = false;
      this.handlePageMutation = this.handlePageMutation.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleFormChange = this.handleFormChange.bind(this);
      this.handleContentLoaded = this.handleContentLoaded.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
    }

    readConfig () {
      const configElement = document.getElementById(configElementId);
      if (!configElement) {
        return {
          notifyText: 'Notify Me',
        };
      }

      try {
        return {
          notifyText: 'Notify Me',
          ...JSON.parse(configElement.textContent),
        };
      } catch (error) {
        console.warn('Failed to parse Postscript Back in Stock settings', error);
        return {
          notifyText: 'Notify Me',
        };
      }
    }

    init () {
      document.addEventListener('theme:content:loaded', this.handleContentLoaded);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);

      this.observer = new MutationObserver(this.handlePageMutation);
      this.observeProductRoots(document);

      const triggers = this.getTriggersWithin(document);
      if (triggers.length) this.queueTriggers(triggers);
    }

    activateInteractionListeners () {
      if (this.listenersActive) return;
      this.listenersActive = true;
      this.injectStyles();
      document.addEventListener('click', this.handleClick, true);
      // A press starts Postscript's teaser render ~100-300ms before the click lands
      document.addEventListener('touchstart', this.handlePress, { capture: true, passive: true });
      document.addEventListener('pointerover', this.handlePress, { passive: true });
      document.addEventListener('variant:change', this.handleVariantChange);
      document.addEventListener('change', this.handleFormChange);
    }

    observeProductRoots (scope) {
      const roots = [];
      if (scope instanceof Element && scope.matches(productRootSelector)) {
        roots.push(scope);
      }
      scope.querySelectorAll?.(productRootSelector).forEach((root) => roots.push(root));

      roots.forEach((root) => {
        if (roots.some((candidate) => candidate !== root && candidate.contains(root))) return;
        if (this.observedRoots.has(root)) return;
        this.observedRoots.add(root);
        this.observer.observe(root, {
          subtree: true,
          childList: true,
        });
      });
    }

    getTriggersWithin (scope) {
      const triggers = [];
      if (scope instanceof Element && scope.matches(triggerSelector)) {
        triggers.push(scope);
      }
      scope.querySelectorAll?.(triggerSelector).forEach((trigger) => triggers.push(trigger));
      return triggers;
    }

    handleContentLoaded (event) {
      const scope = event.detail?.container instanceof Element
        ? event.detail.container
        : event.target;
      if (!(scope instanceof Element)) return;

      this.observeProductRoots(scope);
      const triggers = this.getTriggersWithin(scope);
      if (triggers.length) this.queueTriggers(triggers);
    }

    handleSectionLoad (event) {
      const scope = event.target;
      if (!(scope instanceof Element)) return;

      this.observeProductRoots(scope);
      const triggers = this.getTriggersWithin(scope);
      if (triggers.length) this.queueTriggers(triggers);
    }

    injectStyles () {
      if (document.getElementById(styleElementId)) return;

      const style = document.createElement('style');
      style.id = styleElementId;
      style.textContent = '#ps__bis_teaser_container{display:none!important;}';
      document.head.appendChild(style);
    }

    handlePageMutation (mutations) {
      const addedTriggers = [];

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(triggerSelector)) {
            addedTriggers.push(node);
          }

          if (node.firstElementChild) {
            node.querySelectorAll(triggerSelector).forEach((trigger) => {
              addedTriggers.push(trigger);
            });
          }
        });
      });

      if (addedTriggers.length) {
        this.queueTriggers(addedTriggers);
      }
    }

    handleVariantChange (event) {
      const eventVariant = event.detail ? event.detail.variant : undefined;

      // The theme rewrites the button (e.g. to "Sold Out") synchronously
      // before dispatching this event. Converting to "Notify Me" must also
      // happen synchronously, otherwise the intermediate state gets painted
      // and flickers. The form DOM is not updated yet at this point, so the
      // variant is taken from the event payload instead of the form.
      if (eventVariant && eventVariant.id) {
        document.querySelectorAll(dynamicTriggerSelector).forEach((trigger) => {
          this.refreshTrigger(trigger, eventVariant);
        });
        return;
      }

      this.queueDynamicTriggers();
    }

    handleFormChange (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const isVariantRelated = target.closest(
        'form[action*="/cart/add"], product-template, .product-section, [data-is-quick-view="true"], [id^="QuickModalSection"]',
      );
      if (!isVariantRelated) return;

      this.queueDynamicTriggers();
    }

    queueDynamicTriggers () {
      const dynamicTriggers = document.querySelectorAll(dynamicTriggerSelector);
      if (dynamicTriggers.length) {
        this.queueTriggers(Array.from(dynamicTriggers));
      }
    }

    queueTriggers (triggers) {
      if (!triggers.length) return;
      this.activateInteractionListeners();
      triggers.forEach((trigger) => this.pendingTriggers.add(trigger));
      this.scheduleFlush();
    }

    queueFullRefresh () {
      this.pendingFullRefresh = true;
      this.scheduleFlush();
    }

    scheduleFlush () {
      if (this.refreshQueued) return;
      this.refreshQueued = true;

      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          this.refreshQueued = false;
          this.flushRefresh();
        }, 0);
      });
    }

    flushRefresh () {
      const triggers = this.pendingFullRefresh
        ? Array.from(document.querySelectorAll(triggerSelector))
        : Array.from(this.pendingTriggers).filter((trigger) => trigger.isConnected);

      this.pendingFullRefresh = false;
      this.pendingTriggers.clear();

      if (!triggers.length) return;

      if (!this.popupResolved && !this.popupRefreshChained) {
        this.popupRefreshChained = true;
        this.resolvePopupId().then(() => this.queueFullRefresh());
      }

      triggers.forEach((trigger) => {
        this.refreshTrigger(trigger);
      });
    }

    getPostscriptApi () {
      return window.Postscript?.backInStock || window.postscript?.backInStock || null;
    }

    waitForPostscriptApi () {
      const existingApi = this.getPostscriptApi();
      if (existingApi) return Promise.resolve(existingApi);

      return new Promise((resolve) => {
        const startedAt = Date.now();
        const timer = window.setInterval(() => {
          const api = this.getPostscriptApi();
          if (api) {
            window.clearInterval(timer);
            resolve(api);
            return;
          }

          if (Date.now() - startedAt > 15000) {
            window.clearInterval(timer);
            resolve(null);
          }
        }, 250);
      });
    }

    resolvePopupId () {
      if (this.popupIdPromise) return this.popupIdPromise;

      this.popupIdPromise = this.waitForPostscriptApi()
        .then(async (api) => {
          if (!api) return null;

          try {
            return await api.find();
          } catch (error) {
            console.warn('Postscript Back in Stock find() failed', error);
            return null;
          }
        })
        .then((popupId) => {
          this.popupId = popupId || null;
          this.popupResolved = true;
          return this.popupId;
        });

      return this.popupIdPromise;
    }

    getTriggerState (trigger) {
      if (!this.triggerState.has(trigger)) {
        const textElement = this.getTextElement(trigger);
        this.triggerState.set(trigger, {
          textElement,
          originalText: textElement ? textElement.innerHTML : trigger.innerHTML,
          originalType: trigger.getAttribute('type'),
          originalDisabled: trigger.hasAttribute('disabled'),
          originalAriaDisabled: trigger.getAttribute('aria-disabled'),
          hadDisabledClass: trigger.classList.contains('disabled'),
          appliedState: null,
          lastKnownVariantId: null,
        });
      }

      return this.triggerState.get(trigger);
    }

    getTextElement (trigger) {
      const targetSelector = trigger.getAttribute('data-postscript-bis-text-selector');
      if (targetSelector) {
        const target = trigger.querySelector(targetSelector);
        if (target) return target;
      }

      return trigger.querySelector('span') || trigger;
    }

    getJsonById (id) {
      if (!id) return null;

      const element = document.getElementById(id);
      if (!element) {
        return null;
      }

      // Sections (e.g. quick view) can be re-rendered under the same id,
      // so the cache is valid only while it points at the same DOM element.
      const cached = this.parsedJson.get(id);
      if (cached && cached.element === element) {
        return cached.value;
      }

      let value = null;
      try {
        value = JSON.parse(element.textContent);
      } catch (error) {
        console.warn(`Failed to parse ${id}`, error);
      }

      this.parsedJson.set(id, { element, value });
      return value;
    }

    variantMatches (variants, variantId) {
      return variants.some((variant) => Number(variant.id) === Number(variantId));
    }

    getVariantIdFromQueryString (variants) {
      const variantId = Number(new URLSearchParams(window.location.search).get('variant'));
      if (!variantId || !this.variantMatches(variants, variantId)) return null;
      return variantId;
    }

    getAddToCartForms (trigger) {
      const forms = [];
      const addForm = (form) => {
        if (form && !forms.includes(form)) {
          forms.push(form);
        }
      };
      const formSelector = 'form[action*="/cart/add"]';
      const productSection = trigger.closest('product-template, .product-section, [data-section-id]');

      addForm(trigger.closest(formSelector));
      productSection?.querySelectorAll(formSelector).forEach(addForm);

      if (!forms.length) {
        document.querySelectorAll(formSelector).forEach(addForm);
      }

      return forms;
    }

    getVariantIdFromAddToCartForms (trigger, variants) {
      const forms = this.getAddToCartForms(trigger);

      for (const form of forms) {
        const variantId = Number(form.querySelector('[name="id"]')?.value);
        if (variantId && this.variantMatches(variants, variantId)) {
          return variantId;
        }
      }

      return null;
    }

    getFormVariant (trigger, knownVariant) {
      const sectionId = trigger.getAttribute('data-postscript-bis-section-id');
      const variants = this.getJsonById(`VariantsJson-${sectionId}`) || [];
      const availabilityData = this.getJsonById(`VariantAvailabilityJson-${sectionId}`) || {};
      const isQuickView = Boolean(trigger.closest('[data-is-quick-view="true"], [id^="QuickModalSection"]'));
      const state = this.getTriggerState(trigger);

      // The variant from a variant:change payload is authoritative; remember
      // it so later refreshes without a payload (quantity change, click)
      // resolve the same variant.
      const knownVariantId = knownVariant && this.variantMatches(variants, knownVariant.id)
        ? Number(knownVariant.id)
        : null;
      if (knownVariantId) {
        state.lastKnownVariantId = knownVariantId;
      }
      const rememberedVariantId = state.lastKnownVariantId && this.variantMatches(variants, state.lastKnownVariantId)
        ? state.lastKnownVariantId
        : null;

      // The trigger attribute (server-rendered current variant) is checked
      // before the form: the master select cannot represent sold-out
      // variants because their options are rendered without value/selected,
      // so its value silently falls back to the first available variant.
      const variantId = (
        knownVariantId ||
        rememberedVariantId ||
        (!isQuickView && this.getVariantIdFromQueryString(variants)) ||
        Number(trigger.getAttribute('data-postscript-bis-variant-id')) ||
        this.getVariantIdFromAddToCartForms(trigger, variants) ||
        Number(variants[0]?.id)
      );

      if (!variantId) return null;

      const variant = variants.find((item) => Number(item.id) === variantId) || {
        id: variantId,
        title: trigger.getAttribute('data-postscript-bis-variant-title') || '',
        name: trigger.getAttribute('data-postscript-bis-variant-title') || '',
        available: false,
      };
      const currentAvailability = availabilityData[String(variantId)] || {};

      return {
        ...variant,
        id: variantId,
        title: variant.title || variant.name || trigger.getAttribute('data-postscript-bis-variant-title') || '',
        available: currentAvailability.available === false
          ? false
          : Boolean(variant.available),
        unavailableForSale: currentAvailability.unavailableForSale === true || variant.unavailable_for_sale === true,
      };
    }

    getCurrentVariant (trigger, knownVariant) {
      if (trigger.getAttribute('data-postscript-bis-dynamic') === 'true') {
        return this.getFormVariant(trigger, knownVariant);
      }

      return {
        id: Number(trigger.getAttribute('data-postscript-bis-variant-id')),
        title: trigger.getAttribute('data-postscript-bis-variant-title') || '',
        available: trigger.getAttribute('data-postscript-bis-available') === 'true',
        unavailableForSale: false,
      };
    }

    isTriggerUnavailable (trigger, knownVariant) {
      const variant = this.getCurrentVariant(trigger, knownVariant);
      if (!variant || !variant.id) return false;
      return variant.available !== true || variant.unavailableForSale === true;
    }

    getContext (trigger) {
      const variant = this.getCurrentVariant(trigger);
      const productId = trigger.getAttribute('data-postscript-bis-product-id');
      const productTitle = trigger.getAttribute('data-postscript-bis-product-title') || '';

      const context = {
        product: {
          id: productId ? String(productId) : '',
          name: productTitle,
        },
      };

      if (variant?.id) {
        context.variant = {
          id: String(variant.id),
          name: variant.title || '',
        };
      }

      return context;
    }

    refreshTrigger (trigger, knownVariant) {
      const state = this.getTriggerState(trigger);
      const isDynamic = trigger.getAttribute('data-postscript-bis-dynamic') === 'true';
      const isUnavailable = this.isTriggerUnavailable(trigger, knownVariant);
      const shouldActivate = isUnavailable && this.popupResolved && Boolean(this.popupId);
      const nextAppliedState = shouldActivate
        ? 'active'
        : (isUnavailable ? 'unavailable' : 'available');

      // Static card triggers are only mutated by this script, so unchanged
      // state means no DOM writes are needed. Dynamic (PDP/quick view)
      // triggers are also rewritten by the theme on variant change and must
      // be re-applied even when our computed state is the same.
      if (!isDynamic && state.appliedState === nextAppliedState) return;
      state.appliedState = nextAppliedState;

      if (shouldActivate) {
        this.activateTrigger(trigger);
        return;
      }

      this.deactivateTrigger(trigger, isUnavailable);
    }

    activateTrigger (trigger) {
      this.scheduleWarmUp();
      const state = this.getTriggerState(trigger);
      const textElement = state.textElement;

      trigger.classList.add(activeClass);
      trigger.setAttribute('data-postscript-bis-active', 'true');
      trigger.setAttribute('type', 'button');
      trigger.removeAttribute('disabled');
      trigger.removeAttribute('aria-disabled');
      trigger.classList.add('disabled');

      if (textElement) {
        textElement.innerHTML = this.config.notifyText || 'Notify Me';
      }
    }

    deactivateTrigger (trigger, isUnavailable) {
      const state = this.getTriggerState(trigger);
      const isDynamic = trigger.getAttribute('data-postscript-bis-dynamic') === 'true';
      const wasActive = trigger.getAttribute('data-postscript-bis-active') === 'true';

      trigger.classList.remove(activeClass);
      trigger.removeAttribute('data-postscript-bis-active');

      if (state.originalType === null) {
        trigger.removeAttribute('type');
      } else {
        trigger.setAttribute('type', state.originalType);
      }

      if (isDynamic && !isUnavailable) {
        return;
      }

      if (isDynamic && !wasActive) {
        return;
      }

      if (isDynamic && isUnavailable) {
        trigger.setAttribute('disabled', 'disabled');
        trigger.classList.add('disabled');

        if (state.originalAriaDisabled === null) {
          trigger.removeAttribute('aria-disabled');
        } else {
          trigger.setAttribute('aria-disabled', state.originalAriaDisabled);
        }

        if (state.textElement) {
          state.textElement.innerHTML = window.theme?.strings?.soldOut || state.originalText;
        }

        return;
      }

      if (state.originalDisabled) {
        trigger.setAttribute('disabled', 'disabled');
      } else {
        trigger.removeAttribute('disabled');
      }

      if (state.originalAriaDisabled === null) {
        trigger.removeAttribute('aria-disabled');
      } else {
        trigger.setAttribute('aria-disabled', state.originalAriaDisabled);
      }

      if (state.hadDisabledClass) {
        trigger.classList.add('disabled');
      }

      if (state.textElement) {
        state.textElement.innerHTML = state.originalText;
      }
    }

    getHiddenTeaserContainer () {
      let container = document.getElementById(hiddenTeaserId);
      if (container) return container;

      container = document.createElement('div');
      container.id = hiddenTeaserId;
      container.setAttribute('aria-hidden', 'true');
      container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
      document.body.appendChild(container);
      return container;
    }

    getTeaserKey (popupId, context) {
      const productId = context?.product?.id || '';
      const variantId = context?.variant?.id || '';
      return `${popupId}:${productId}:${variantId}`;
    }

    ensureHiddenTeaser (popupId, context) {
      const teaserKey = this.getTeaserKey(popupId, context);
      if (this.hiddenTeaserPromise && this.hiddenTeaserKey === teaserKey) {
        return this.hiddenTeaserPromise;
      }

      this.hiddenTeaserKey = teaserKey;
      this.hiddenTeaserPromise = this.waitForPostscriptApi().then(async (api) => {
        if (!api || !popupId) return false;

        try {
          const container = this.getHiddenTeaserContainer();
          container.innerHTML = '';
          await api.renderTeaser(popupId, container, context);
          return true;
        } catch (error) {
          console.warn('Postscript Back in Stock renderTeaser() failed', error);
          this.hiddenTeaserPromise = null;
          this.hiddenTeaserKey = null;
          return false;
        }
      });

      return this.hiddenTeaserPromise;
    }

    // Speed (Van 2026-10-22 "make sure all modals load fast"): Postscript's popup only opens after renderTeaser(), which
    // took ~1.4s on the first tap (one-time setup ~1s, then ~0.35s a product). Once the page is idle the teaser is
    // rendered for the first Notify me on screen (on a product page: that product, so its tap opens straight away),
    // which also gets the one-time setup out of the way for the rest.
    scheduleWarmUp () {
      if (this.warmQueued) return;
      this.warmQueued = true;
      const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1500));
      idle(() => {
        const triggers = Array.from(document.querySelectorAll(`${triggerSelector}[data-postscript-bis-active="true"]`));
        const onScreen = triggers.find((trigger) => {
          const rect = trigger.getBoundingClientRect();
          return rect.width && rect.bottom > 0 && rect.top < window.innerHeight;
        });
        const trigger = triggers.find((item) => item.getAttribute('data-postscript-bis-dynamic') === 'true') || onScreen;
        if (trigger) this.warm(trigger);
      }, { timeout: 4000 });
    }

    async warm (trigger) {
      const popupId = await this.resolvePopupId();
      if (!popupId || !trigger.isConnected) return;
      this.ensureHiddenTeaser(popupId, this.getContext(trigger));
    }

    handlePress (event) {
      if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
      const trigger = event.target instanceof Element ? event.target.closest(triggerSelector) : null;
      if (!trigger || trigger.getAttribute('data-postscript-bis-active') !== 'true') return;
      this.warm(trigger);
    }

    async handleClick (event) {
      const trigger = event.target.closest(triggerSelector);
      if (!trigger || trigger.getAttribute('data-postscript-bis-active') !== 'true') return;

      event.preventDefault();
      event.stopPropagation();

      const popupId = await this.resolvePopupId();
      const api = await this.waitForPostscriptApi();
      const context = this.getContext(trigger);

      if (!api || !popupId) {
        this.getTriggerState(trigger).appliedState = 'unavailable';
        this.deactivateTrigger(trigger, true);
        return;
      }

      trigger.setAttribute('aria-busy', 'true');
      trigger.classList.add('postscript-bis-trigger--loading');
      const teaserReady = await this.ensureHiddenTeaser(popupId, context);
      trigger.removeAttribute('aria-busy');
      trigger.classList.remove('postscript-bis-trigger--loading');
      if (!teaserReady) return;

      api.open(popupId, context);
    }
  }

  const controller = new PostscriptBackInStock();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => controller.init());
  } else {
    controller.init();
  }
})();
